'use client'

import Image from 'next/image'
import { ReactNode, useMemo, useState } from 'react'
import { useDropzone } from 'react-dropzone'

import { RotateCcw, Trash2, Upload } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { toast } from 'sonner'

import { cn, isFieldInvalid } from '@/utils'

import { FieldWrapper } from './field-wrapper'
import { useFieldContext } from './form'

export type ExistingImage = { id: number | string; image_url: string }
export type FileUploadValue = (ExistingImage | File)[]

type Radius = '2xl' | 'full' | 'lg' | 'md' | 'sm' | 'xl'

type Size = '2xl' | '3xl' | 'lg' | 'md' | 'sm' | 'xl'

const formatMegabytes = (bytes: number) => Math.round(bytes / 1024 / 1024)

export const MultiImagesUpload = ({
  allowDelete = true,
  allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'],
  disabled = false,
  fullWidth = false,
  height = 140, // default to 140
  icon,
  isLoading = false,
  label,
  maxImagesCount = 20,
  maxImageSize = 5 * 1024 * 1024,
  maxImagesSize = 20 * 1024 * 1024,
  setDeletedImagesIds,
  width = 160 // new width prop, default to 200
}: {
  allowDelete?: boolean
  allowedTypes?: string[]
  disabled?: boolean
  fullWidth?: boolean
  height?: number
  icon?: ReactNode
  isLoading?: boolean
  label?: string
  maxImagesCount?: number
  maxImageSize?: number
  maxImagesSize?: number
  onDeleteExistingImage?: (id: number | string) => void
  radius?: Radius
  setDeletedImagesIds: (ids: ((prev: number[]) => number[]) | number[]) => void
  size?: Size
  width?: number // new width prop
}) => {
  const t = useTranslations('components.dataTable.multiImagesUpload')
  const field = useFieldContext<FileUploadValue>()
  const isInvalid = isFieldInvalid(field)
  // ---------------------------------------------------------------------------------

  // ---------------------------------------------------------------------------------

  // ---------------------------------------------------------------------------------

  const newFiles = useMemo(() => {
    return (field.state.value || []).filter(
      (item): item is File => item instanceof File
    )
  }, [field.state.value])
  // ---------------------------------------------------------------------------------

  const fieldExistingImages = useMemo(() => {
    return (field.state.value || []).filter(
      (item): item is ExistingImage => !(item instanceof File)
    )
  }, [field.state.value])
  // ---------------------------------------------------------------------------------

  // ---------------------------------------------------------------------------------

  const totalSize = useMemo(() => {
    let size = 0
    newFiles.forEach((f) => (size += f.size))
    return size
  }, [newFiles])
  // ---------------------------------------------------------------------------------

  const handleFilesChange = (acceptedFiles: File[]) => {
    if (disabled) return
    const errors: string[] = []

    // File type validation

    const invalidTypeFiles = acceptedFiles.filter(
      (f) => !allowedTypes.includes(f.type)
    )
    if (invalidTypeFiles.length > 0) {
      errors.push(t('invalidFileType'))
    }

    if (maxImagesCount <= 0 || field.state.value?.length >= maxImagesCount) {
      const maxImagesParams = { count: `${maxImagesCount}` }
      errors.push(t('maxImages', maxImagesParams))
    }
    const validFiles = acceptedFiles.filter((f) => {
      if (allowedTypes?.length && !allowedTypes.includes(f.type)) return false
      if (f.size > maxImageSize) {
        const maxSizeParams = {
          fileName: f.name,
          maxImageSizeMb: `${formatMegabytes(maxImageSize)}`
        }
        errors.push(t('maxSize', maxSizeParams))
        return false
      }
      return true
    })
    const newTotalSize =
      totalSize + validFiles.reduce((acc, f) => acc + f.size, 0)
    if (newTotalSize > maxImagesSize) {
      const totalSizeParams = {
        maxImagesSizeMb: String(formatMegabytes(maxImagesSize))
      }
      errors.push(t('totalSize', totalSizeParams))
    }
    const filesToAdd = validFiles.slice(0, maxImagesCount)
    if (errors.length > 0) {
      toast.error(errors.join(' '))
      return
    }
    const updatedFiles = [...newFiles, ...filesToAdd]
    field.handleChange([...fieldExistingImages, ...updatedFiles])
  }
  // ---------------------------------------------------------------------------------

  const { getInputProps, getRootProps, isDragActive } = useDropzone({
    accept: { 'image/*': [] },
    onDrop: handleFilesChange
  })
  // ---------------------------------------------------------------------------------

  const handleRemove = (idx: number) => {
    const newArr = [...(field.state.value || [])]
    const [removed] = newArr.splice(idx, 1)
    field.handleChange(newArr)
    if (removed && typeof removed === 'object' && 'image_url' in removed) {
      setDeletedImages((prev) => [...prev, removed])
      if ('id' in removed && removed.id !== undefined && removed.id !== null) {
        const idNum =
          typeof +removed.id === 'number' ? removed.id : Number(removed.id)
        setDeletedImagesIds((prev: number[]) => [...prev, +idNum])
      }
    }
  }
  // ---------------------------------------------------------------------------------

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleRestore = (img: any) => {
    if ((field.state.value?.length || 0) >= maxImagesCount) return
    field.handleChange([...(field.state.value || []), img])
    setDeletedImages((prev) => prev.filter((d) => d !== img))
    if ('id' in img && img.id !== undefined && img.id !== null) {
      const idNum = typeof +img.id === 'number' ? img.id : Number(img.id)
      setDeletedImagesIds((prev: number[]) =>
        prev.filter((x: number) => x !== idNum)
      )
    }
  }
  // ---------------------------------------------------------------------------------

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [deletedImages, setDeletedImages] = useState<any[]>([])
  // ---------------------------------------------------------------------------------

  return (
    <FieldWrapper
      className={cn(fullWidth && 'w-full')}
      classNames={{ childrenWrapper: 'gap-2' }}
      field={field}
      label={label}
    >
      <div
        className="flex flex-col gap-2"
        data-invalid={isInvalid || undefined}
      >
        {/* Uploaded Images Grid */}

        <div className="flex flex-wrap gap-6">
          {(field.state.value || []).length > 0 &&
            (field.state.value || []).map((img, idx) => {
              let url = ''
              if (img instanceof File) {
                url = URL.createObjectURL(img)
              } else if ('file' in img && img.file instanceof File) {
                url = URL.createObjectURL(img.file)
              } else if ('image_url' in img) {
                url = img.image_url
              }
              return (
                <div
                  className={`group relative overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm${fullWidth ? 'w-full' : ''}`}
                  key={idx}
                  style={{ height: height, width: fullWidth ? '100%' : width }}
                >
                  <Image
                    alt=""
                    className="h-full w-full rounded-xl object-cover"
                    fill={false}
                    height={height}
                    sizes="(max-width: 768px) 100vw, 200px"
                    src={url}
                    width={width}
                  />
                  {allowDelete && !disabled && (
                    <button
                      className="text-secondary focus:ring-secondary absolute end-1.5 top-1.5 z-10 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-white/80 opacity-0 shadow-lg transition group-hover:opacity-100 hover:scale-110 hover:bg-white focus:ring-2 focus:outline-none"
                      disabled={isLoading}
                      onClick={() => handleRemove(idx)}
                      title={t('removeImage')}
                      type="button"
                    >
                      <Trash2 className="text-destructive size-4" />
                    </button>
                  )}
                  {isLoading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
                      <span className="loader spinner-border border-t-primary-500 inline-block h-6 w-6 animate-spin rounded-full border-4 border-gray-300"></span>
                    </div>
                  )}
                </div>
              )
            })}
          {!disabled && (
            <div
              {...getRootProps()}
              className={
                `${fullWidth ? 'w-full' : 'w-32'} flex cursor-pointer flex-col items-center justify-center rounded border border-dashed border-gray-300 bg-gray-50 p-2 transition-colors duration-200` +
                (isDragActive ? ' border-primary-500 bg-primary-50' : '') +
                (isLoading ? ' pointer-events-none opacity-60' : '')
              }
              data-invalid={isInvalid || undefined}
              style={{
                height,
                minHeight: 40,
                width: fullWidth ? '100%' : width
              }}
            >
              <input
                {...getInputProps()}
                className="hidden"
                disabled={isLoading}
              />
              <div className="flex flex-col items-center justify-center gap-1">
                {icon || (
                  <Upload
                    className="text-gray-400"
                    height={16}
                    strokeWidth={2}
                    width={16}
                  />
                )}
                <span className="text-xs text-gray-500">
                  {isDragActive ? t('dropHere') : t('clickOrDrag')}
                </span>
                <span className="text-[10px] text-gray-400">
                  {t('maxImagesAndSize', {
                    count: String(maxImagesCount),
                    maxImageSizeMb: String(formatMegabytes(maxImageSize))
                  })}
                </span>
                {isLoading && (
                  <span className="loader spinner-border border-t-primary-500 mt-2 inline-block h-6 w-6 animate-spin rounded-full border-4 border-gray-300"></span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Removed Images Section */}
        {deletedImages.length > 0 && (
          <div className="mt-1 rounded border border-dashed border-gray-200 bg-gray-50 p-1">
            <div className="mb-1 text-[10px] font-semibold text-gray-500">
              {t('removedSection')}
            </div>
            <div className="flex flex-wrap gap-6">
              {deletedImages.map((img, idx) => {
                const isDisabled =
                  (field.state.value?.length || 0) >= maxImagesCount ||
                  isLoading
                return (
                  <div
                    className="relative overflow-hidden rounded border border-gray-200 bg-white opacity-60"
                    key={img.id || idx}
                    style={{
                      height: height,
                      width: fullWidth ? '100%' : width
                    }}
                  >
                    <Image
                      alt={t('deletedAlt')}
                      className="h-full w-full rounded-xl object-cover"
                      height={height}
                      src={img.image_url}
                      width={width}
                    />
                    <button
                      className={`absolute end-1.5 top-1.5 ${isDisabled ? 'cursor-not-allowed bg-gray-400' : 'bg-green-500 hover:bg-green-600'} flex h-8 w-8 items-center justify-center rounded-full text-white shadow-lg transition hover:scale-110 focus:ring-2 focus:ring-green-400 focus:outline-none`}
                      disabled={isDisabled}
                      onClick={() => handleRestore(img)}
                      title={
                        isDisabled
                          ? t('maxReachedTooltip')
                          : t('restoreTooltip')
                      }
                      type="button"
                    >
                      <RotateCcw height={20} strokeWidth={2.2} width={20} />
                    </button>
                  </div>
                )
              })}
            </div>
          </div>
        )}
        {/* Dropzone Area (moved after images) */}

        {/* Fallback file input for manual selection (hidden visually) */}
        <input
          accept="image/*"
          className="hidden"
          disabled={isLoading}
          multiple
          onChange={(e) => {
            const files = Array.from(e.target.files || [])
            const maxCount = 3 // or your prop
            const maxSize = 5 * 1024 * 1024 // 5MB, or your prop
            let current = field.state.value || []
            const errors = []
            for (const file of files) {
              if (current.length >= maxCount) {
                errors.push(t('maxCountReached'))
                break
              }
              if (file.size > maxSize) {
                errors.push(t('fileTooLarge', { fileName: file.name }))
                continue
              }
              current = [...current, file]
            }
            if (errors.length) {
              toast.error(errors.join('\n'))
            } else {
              field.handleChange(current)
            }
            e.target.value = ''
          }}
          type="file"
        />
      </div>
    </FieldWrapper>
  )
}
